import { Link } from 'react-router-dom'
import { ArrowRight, ArrowUpRight, Clock, HeartHandshake, MapPin, Users } from 'lucide-react'
import ScrollAnimation from '../components/ScrollAnimation'
import { players } from '../data/content'

const Home = () => {
  const stats = [
    { value: '2012', label: 'Inicio en México' },
    { value: '25', label: 'Jugadores en el Open 2026' },
    { value: '4', label: 'Países en esa sede' },
    { value: '8', label: 'Estados con apoyo' },
  ]

  const who = [
    {
      title: 'Discapacidad motriz',
      text: 'Posturas, grips y recorridos adaptados. El swing se construye alrededor de tu cuerpo, no al revés.',
    },
    {
      title: 'Discapacidad sensorial',
      text: 'Guías, referencias táctiles y acompañamiento en el campo para jugar con confianza.',
    },
    {
      title: 'Discapacidad intelectual',
      text: 'Ritmo propio, repetición clara y un espacio donde el juego también es rehabilitación.',
    },
  ]

  const steps = [
    {
      n: '01',
      title: 'Escríbenos o llama',
      text: 'Cuéntanos tu situación. No hace falta experiencia previa ni equipo propio.',
    },
    {
      n: '02',
      title: 'Ven a Xochimilco',
      text: 'Martes o jueves, de 9:30 a 13:00, en el Centro Deportivo Xochimilco.',
    },
    {
      n: '03',
      title: 'Juega con nosotros',
      text: 'Instructores especializados, palos adaptados y una comunidad que te espera.',
    },
  ]

  const stories = [
    {
      id: 1,
      title: 'De la rehabilitación de posguerra a un deporte con reglas',
      excerpt: '1953, 1954 y el camino hasta la Regla 25. El golf adaptado no nació en un club.',
      category: 'Historia',
      to: '/blog/origenes-del-golf-adaptado',
    },
    {
      id: 2,
      title: 'México, de un hermano al Adaptive Open',
      excerpt: '2012 en casa. 2024 con 13 jugadores. 2026 con 25, de cuatro países, en el Club de Golf México.',
      category: 'México',
      to: '/blog/mexico-2012-2026',
    },
    {
      id: 3,
      title: 'Quienes ya compiten con el nombre de México',
      excerpt: 'Un campeón mundial B1, el mejor Neto del país y la primera delegación sorda en unos Deaflympics.',
      category: 'Jugadores',
      to: '/blog/mexicanos-en-el-circuito',
    },
  ]

  return (
    <div>
      <section className="relative overflow-hidden bg-fairway text-cream">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/campo.png')" }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-fairway/50" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-fairway/95 via-fairway/70 to-leaf/20" aria-hidden="true" />

        <div className="relative mx-auto grid min-h-[calc(100vh-5rem)] max-w-7xl items-end gap-12 px-4 pb-16 pt-16 sm:px-6 md:items-center md:pb-24 md:pt-20 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-7">
            <p className="eyebrow text-sun">México · Xochimilco · Desde 2012</p>
            <h1 className="mt-5 font-serif text-5xl font-semibold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
              El golf también
              <br />
              es para <em className="font-extrabold not-italic text-sun">ti</em>.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-cream/80 md:text-xl">
              Clases gratuitas de golf adaptado para personas con discapacidad motriz, sensorial e intelectual.
              El deporte como rehabilitación, y la comunidad como punto de partida.
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link to="/contact" className="btn-brass px-7 py-3.5 text-base">
                Quiero jugar
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/history" className="btn-secondary px-7 py-3.5 text-base">
                Nuestra historia
              </Link>
            </div>
            <p className="mt-8 inline-flex items-center gap-2 rounded-full border border-cream/15 bg-white/5 px-4 py-2 text-sm text-cream/80">
              <Clock className="h-4 w-4 text-sun" aria-hidden="true" />
              Martes y jueves · 9:30–13:00
            </p>
          </div>

          <aside className="lg:col-span-5 lg:justify-self-end">
            <div className="max-w-sm rounded-3xl border border-white/15 bg-leaf/80 p-6 backdrop-blur-sm">
              <p className="text-sm text-cream/60">Próximo paso</p>
              <p className="mt-2 font-serif text-2xl leading-snug">
                Llega al Deportivo Xochimilco. Trae ganas. El resto lo ponemos nosotros.
              </p>
              <div className="mt-6 space-y-3 text-sm text-cream/75">
                <p className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-sun" aria-hidden="true" />
                  Francisco Goitia s/n, Xochimilco, CDMX
                </p>
                <p className="flex items-start gap-2">
                  <Users className="mt-0.5 h-4 w-4 shrink-0 text-sun" aria-hidden="true" />
                  Principiantes y avanzados, todas las edades
                </p>
              </div>
              <Link
                to="/golf"
                className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-sun hover:text-white"
              >
                Ver horarios y torneos
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-y border-fairway/10 bg-cream">
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-y divide-fairway/10 sm:grid-cols-4 sm:divide-y-0">
          {stats.map((stat) => (
            <div key={stat.label} className="px-6 py-8 text-center">
              <p className="font-serif text-3xl text-leaf md:text-4xl">{stat.value}</p>
              <p className="mt-1 text-sm text-mist">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section-pad mx-auto max-w-7xl">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <ScrollAnimation>
            <p className="eyebrow">Misión</p>
            <h2 className="display mt-4 text-4xl md:text-5xl">
              El golf como herramienta, no como privilegio.
            </h2>
          </ScrollAnimation>
          <ScrollAnimation delay={0.1}>
            <p className="text-lg leading-relaxed text-mist">
              Atendemos a personas con discapacidad sin distinción, usando el golf para rehabilitación
              física y psicológica. En Xochimilco damos clases regulares; en ocho estados acompañamos
              a jugadores foráneos para que nadie se quede fuera del fairway.
            </p>
            <p className="mt-6 border-l-2 border-leaf pl-5 font-sans text-xl font-medium italic leading-relaxed text-ink">
              “Empezó con un hermano, un diagnóstico y la negativa a dejar de jugar. Así nació el golf adaptado en México.”
            </p>
            <p className="mt-3 text-sm text-mist">Alejandro Florero · Presidente</p>
          </ScrollAnimation>
        </div>
      </section>

      <section className="bg-cream">
        <div className="section-pad mx-auto max-w-7xl">
          <ScrollAnimation>
            <div className="mb-12 max-w-2xl">
              <p className="eyebrow">Para quién</p>
              <h2 className="display mt-4 text-4xl md:text-5xl">
                Un deporte que se adapta a ti.
              </h2>
            </div>
          </ScrollAnimation>
          <div className="grid gap-6 md:grid-cols-3">
            {who.map((item, index) => (
              <ScrollAnimation key={item.title} delay={index * 0.08}>
                <article className="flex h-full flex-col rounded-3xl bg-white p-8 ring-1 ring-leaf/10">
                  <span className="font-serif text-4xl text-leaf">0{index + 1}</span>
                  <h3 className="mt-6 font-serif text-2xl text-ink">{item.title}</h3>
                  <p className="mt-3 flex-1 leading-relaxed text-mist">{item.text}</p>
                </article>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad mx-auto max-w-7xl">
        <ScrollAnimation>
          <div className="mb-14 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="eyebrow">Cómo empezar</p>
              <h2 className="display mt-4 text-4xl md:text-5xl">Tres pasos para empezar.</h2>
            </div>
            <Link to="/contact" className="btn-primary self-start md:self-auto">
              Escríbenos
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </ScrollAnimation>
        <ol className="grid gap-10 md:grid-cols-3">
          {steps.map((step, index) => (
            <ScrollAnimation key={step.n} delay={index * 0.08}>
              <li>
                <p className="font-serif text-sm tracking-eyebrow text-leaf">{step.n}</p>
                <h3 className="mt-3 font-serif text-2xl">{step.title}</h3>
                <p className="mt-3 leading-relaxed text-mist">{step.text}</p>
              </li>
            </ScrollAnimation>
          ))}
        </ol>
      </section>

      <section className="bg-leaf text-cream">
        <div className="section-pad mx-auto max-w-7xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <ScrollAnimation>
              <p className="eyebrow text-sun">Línea de tiempo</p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl">De un hermano a un movimiento nacional.</h2>
              <p className="mt-5 max-w-lg text-cream/75 leading-relaxed">
                Lo que empezó en 2012 hoy conecta a golfistas de todo el país con el circuito mundial de golf adaptado.
              </p>
              <Link to="/history" className="btn-brass mt-8">
                Recorrer la historia
                <ArrowRight className="h-4 w-4" />
              </Link>
            </ScrollAnimation>
            <ScrollAnimation delay={0.1}>
              <ol className="space-y-0 border-l border-cream/20 pl-8">
                {[
                  { year: '2012', text: 'Alejandro Florero inicia el golf adaptado en México.' },
                  { year: '2023', text: 'La Regla 25 entra al libro oficial de The R&A y la USGA.' },
                  { year: '2025', text: 'México debuta en el golf de los Deaflympics, en Tokio.' },
                  { year: '2026', text: 'FMG Adaptive Open: 25 jugadores de México, Guatemala, Argentina y Colombia.' },
                ].map((event) => (
                  <li key={event.year} className="relative py-5">
                    <span className="absolute -left-[2.15rem] top-7 h-3 w-3 rounded-full bg-sun" />
                    <p className="font-serif text-xl text-sun">{event.year}</p>
                    <p className="mt-1 text-cream/80">{event.text}</p>
                  </li>
                ))}
              </ol>
            </ScrollAnimation>
          </div>
        </div>
      </section>

      <section className="bg-cream">
        <div className="section-pad mx-auto max-w-7xl">
          <ScrollAnimation>
            <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <p className="eyebrow">En el circuito</p>
                <h2 className="display mt-4 text-4xl md:text-5xl">México ya tiene nombres en el ranking.</h2>
              </div>
              <Link to="/community#jugadores" className="text-sm font-semibold text-leaf hover:text-fairway">
                Ver los seis perfiles →
              </Link>
            </div>
          </ScrollAnimation>
          <div className="grid gap-5 md:grid-cols-3">
            {players.slice(0, 3).map((player, index) => (
              <ScrollAnimation key={player.name} delay={index * 0.08} className="h-full">
                <article className="flex h-full flex-col rounded-3xl bg-white p-7 ring-1 ring-leaf/10">
                  {player.photo && (
                    <img
                      src={player.photo}
                      alt={player.photoAlt || player.name}
                      className={`mb-5 aspect-[3/4] w-full rounded-2xl object-cover ${player.photoClass || 'object-top'}`}
                    />
                  )}
                  <p className="text-xs font-semibold uppercase tracking-wide text-leaf">{player.rank}</p>
                  <h3 className="mt-4 font-serif text-2xl leading-snug">{player.name}</h3>
                  <p className="mt-2 text-sm text-mist">{player.place} · {player.category}</p>
                  <p className="mt-4 flex-1 leading-relaxed text-mist">{player.summary}</p>
                </article>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad mx-auto max-w-7xl">
        <ScrollAnimation>
          <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="eyebrow">Historias</p>
              <h2 className="display mt-4 text-4xl md:text-5xl">Lo que nos define.</h2>
            </div>
            <Link to="/blog" className="text-sm font-semibold text-leaf hover:text-fairway">
              Ver el blog →
            </Link>
          </div>
        </ScrollAnimation>
        <div className="grid gap-6 lg:grid-cols-3">
          {stories.map((story, index) => (
            <ScrollAnimation key={story.id} delay={index * 0.08} className="h-full">
              <Link
                to={story.to}
                className="group flex h-full flex-col rounded-3xl bg-white p-7 ring-1 ring-fairway/10"
              >
                <span className="text-xs font-semibold uppercase tracking-wide text-leaf">
                  {story.category}
                </span>
                <h3 className="mt-5 font-serif text-2xl leading-snug group-hover:text-leaf">
                  {story.title}
                </h3>
                <p className="mt-3 flex-1 text-mist">{story.excerpt}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-leaf">
                  Leer
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </ScrollAnimation>
          ))}
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-cream px-8 py-16 md:px-16 md:py-20">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="eyebrow">Comunidad</p>
              <h2 className="display mt-4 text-4xl md:text-5xl">
                El green está abierto.
              </h2>
              <p className="mt-5 max-w-md text-lg text-mist">
                Jugadoras y jugadores, familias e instructores. Si quieres sumarte —a jugar, a enseñar o a apoyar— hay un lugar para ti.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:justify-end">
              <Link to="/contact" className="btn-primary px-7 py-3.5 text-base">
                <HeartHandshake className="h-5 w-5" />
                Contáctanos
              </Link>
              <Link
                to="/community"
                className="inline-flex items-center justify-center rounded-full border border-leaf/25 px-7 py-3.5 text-base font-semibold text-leaf hover:bg-white"
              >
                Ver la comunidad
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
